/* eslint-disable no-console */
/* eslint-disable react/no-array-index-key */
import React, { useContext, useState, useEffect, useRef, createRef } from 'react';
import PropTypes from 'prop-types';
import { buildParams, parseURL } from '../../../global/js/url';
import SearchContext from '../data/FunnelbackContext';
import SearchCheckbox from './_SearchCheckbox';
import styles from '../../css/functional.module.scss';

/**
 * Renders filter button and list of checkboxes and handles logic related to them.
 *
 *  @param {array} facetData An array of all filter data
 *  @param {number} limit The number of items to display
 *  @param {bool} allFacets The flag to show all facets
 *  @param {number}  debounceDelay The number of milliseconds to wait before fetching
 *
 * @returns {JSX.Element}
 * @constructor
 */

function SearchFilter({ facetData, limit, allFacets, debounceDelay }) {
    const { fetchResults, queryString, setQueryString, selectedFacets, setSelectedFacets, facetsMap } = useContext(SearchContext);

    const [openIndexes, setOpenIndexes] = useState([]);
    const [currentIndex, setCurrentIndex] = useState();
    const facetRef = useRef([]);
    const [doFetch, setDoFetch] = useState(false);
    const controller = useRef();

    const facetsLength = facetData?.length || 0;
    if (facetRef.current.length !== facetsLength) {
        facetRef.current = Array(facetsLength)
            .fill()
            .map((_, i) => facetRef.current[i] || createRef());
    }

    // Show/Hide facet
    const onClickHandler = (event) => {
        if (currentIndex === event.target.getAttribute('data-index')) {
            setCurrentIndex(undefined);
        } else {
            setCurrentIndex(event.target.getAttribute('data-index'));
        }

        if (allFacets && event?.target) {
            const idx = parseInt(event.target.getAttribute('data-index'), 10);
            if (openIndexes?.includes(idx)) {
                setOpenIndexes(openIndexes.filter((e) => e !== idx));
            } else {
                setOpenIndexes([...openIndexes, idx]);
            }
        }
    };

    // Handle outside facet click
    const handleClickOutsideFacets = (event) => {
        if (facetRef?.current[currentIndex]?.current && !facetRef?.current[currentIndex]?.current?.contains(event.target)) {
            if (!event?.target?.classList?.contains('funnelback-instant-search-facets__group')) {
                setCurrentIndex(undefined);
            }
        }
    };

    const handleFocusOutsideFacets = (event) => {
        if (facetRef?.current[currentIndex]?.current && !facetRef?.current[currentIndex]?.current?.contains(event.target)) {
            if (!allFacets) {
                setCurrentIndex(undefined);
            }
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutsideFacets, true);
        document.addEventListener('focus', handleFocusOutsideFacets, true);
        return () => {
            document.removeEventListener('click', handleClickOutsideFacets, true);
            document.removeEventListener('focus', handleFocusOutsideFacets, true);
        };
    });

    // Checkbox change handler
    const onSelectCheckbox = (event) => {
        const facetValue = event.target.getAttribute('data-name');
        // Extracting the right
        // eslint-disable-next-line no-useless-escape
        const regex = new RegExp(`.*(f\..*?)${facetValue.replace(/\s/g, '\\+').replace(',', '%2C')}.*`, 'gi');

        const facetGroupName =
            event.target.parentElement.getAttribute('data-paramname') ||
            event.target.getAttribute('data-url').replace(regex, '$1').replace(/%7C/g, '|').replace(/=/g, '').replace('+', ' ');

        if (facetGroupName in selectedFacets) {
            if (selectedFacets[`${facetGroupName}`].includes(facetValue)) {
                setSelectedFacets({
                    ...selectedFacets,
                    [`${facetGroupName}`]: selectedFacets[`${facetGroupName}`].filter((el) => el !== facetValue),
                });
            } else {
                const GropupFacets = [...selectedFacets[`${facetGroupName}`]];
                GropupFacets.push(facetValue);
                setSelectedFacets({ ...selectedFacets, [`${facetGroupName}`]: GropupFacets });
            }
        } else {
            setSelectedFacets({ ...selectedFacets, [`${facetGroupName}`]: [facetValue] });
        }

        setDoFetch(true);
    };

    useEffect(() => {
        const fetchData = async () => {
            controller?.current?.abort();
            controller.current = new AbortController();
            const urlParams = parseURL(queryString);
            const newParams = buildParams({ ...urlParams, ...selectedFacets }, true, true);

            setQueryString(newParams);
            await fetchResults(null, `${newParams}`, controller.current.signal);
            setDoFetch(false);
        };

        const delayDebounceFn = setTimeout(async () => {
            if (selectedFacets && doFetch) {
                fetchData().catch(console.error);
            }
        }, debounceDelay);

        return () => {
            clearTimeout(delayDebounceFn);
        };
    }, [doFetch]);

    const facetLimit = limit || facetData.length;

    // Return nothing if facetData is empty
    if (!facetData || facetData.length === 0) {
        return '';
    }

    return (
        <>
            {facetData.map((facet, index) => {
                return (
                    index < facetLimit && (
                        <React.Fragment key={`${allFacets ? 'all-' : ''}facet-${facet.name}-${index}`}>
                            <button
                                id={`${allFacets ? 'all-' : ''}funnelback-instant-search-facets-group-${index}`}
                                className={`funnelback-instant-search-facets__group ${styles.facetGroup} ${
                                    (!allFacets && index === parseInt(currentIndex, 10)) || (allFacets && openIndexes?.includes(index))
                                        ? 'active'
                                        : ''
                                }`}
                                type="button"
                                data-index={index}
                                aria-expanded={
                                    !!((!allFacets && index === parseInt(currentIndex, 10)) || (allFacets && openIndexes?.includes(index)))
                                }
                                aria-controls={`${allFacets ? 'all-' : ''}funnelback-instant-search-facets-group-list-${index}`}
                                tabIndex={0}
                                onClick={onClickHandler}
                            >
                                {facet.name}
                                <svg className={`funnelback-instant-search-facets__icon ${styles.facetIcon}`}>
                                    <use href="#he-chevron-down" />
                                </svg>
                            </button>
                            {((!allFacets && index === parseInt(currentIndex, 10)) || (allFacets && openIndexes?.includes(index))) && (
                                <ul
                                    ref={facetRef.current[index]}
                                    id={`${allFacets ? 'all-' : ''}funnelback-instant-search-facets-group-list-${index}`}
                                    role="region"
                                    aria-labelledby={`${allFacets ? 'all-' : ''}funnelback-instant-search-facets-group-${index}`}
                                    className={`funnelback-instant-search-facets__list ${styles.facetList}`}
                                >
                                    {facet.allValues.map((el, idx) => (
                                        <li
                                            key={`${allFacets ? 'all-' : ''}item-${facet.name}-${el.data}-${index}-${idx}`}
                                            data-paramname={facetsMap
                                                ?.filter((element) => element.name === facet.name)
                                                .map((element) => element.allQueryStringParamNames)}
                                            className={`funnelback-instant-search-facets__item ${styles.facetItem}`}
                                        >
                                            <SearchCheckbox checkboxData={el} onSelectCheckbox={onSelectCheckbox} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </React.Fragment>
                    )
                );
            })}
        </>
    );
}
SearchFilter.propTypes = {
    facetData: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            unselectAllUrl: PropTypes.string,
            guessedDisplayType: PropTypes.string,
            allValues: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.string,
                    label: PropTypes.string,
                    count: PropTypes.number,
                    selected: PropTypes.bool,
                    toggleUrl: PropTypes.string,
                })
            ),
        })
    ),
    limit: PropTypes.number,
    allFacets: PropTypes.bool,
    debounceDelay: PropTypes.number,
};

SearchFilter.defaultProps = {
    facetData: [],
    limit: undefined,
    allFacets: false,
    debounceDelay: 500,
};

export default SearchFilter;
